import type { Mesh } from "./mesh3d";

// Quadric error metric edge collapse (Garland & Heckbert). Edges whose collapse would flip a face, pinch the surface or
// tear a hole are skipped, so a closed mesh stays closed. Expects a welded mesh (shared vertices).

type Q = Float64Array; // symmetric 4x4 as 10 numbers: a2 ab ac ad b2 bc bd c2 cd d2

const addQ = (a: Q, b: Q): Q => { const r = new Float64Array(10); for (let i = 0; i < 10; i++) r[i] = a[i] + b[i]; return r; };
const planeQ = (a: number, b: number, c: number, d: number, w: number): Q => Float64Array.of(a * a * w, a * b * w, a * c * w, a * d * w, b * b * w, b * c * w, b * d * w, c * c * w, c * d * w, d * d * w);
const evalQ = (q: Q, x: number, y: number, z: number) =>
  q[0] * x * x + 2 * q[1] * x * y + 2 * q[2] * x * z + 2 * q[3] * x + q[4] * y * y + 2 * q[5] * y * z + 2 * q[6] * y + q[7] * z * z + 2 * q[8] * z + q[9];

function solve(q: Q): [number, number, number] | null {
  // Minimise vᵀQv: solve the 3x3 system A p = -b.
  const a = q[0], b = q[1], c = q[2], e = q[4], f = q[5], h = q[7];
  const det = a * (e * h - f * f) - b * (b * h - f * c) + c * (b * f - e * c);
  if (Math.abs(det) < 1e-12) return null;
  const r0 = -q[3], r1 = -q[6], r2 = -q[8];
  const x = (r0 * (e * h - f * f) - b * (r1 * h - f * r2) + c * (r1 * f - e * r2)) / det;
  const y = (a * (r1 * h - f * r2) - r0 * (b * h - f * c) + c * (b * r2 - r1 * c)) / det;
  const z = (a * (e * r2 - r1 * f) - b * (b * r2 - r1 * c) + r0 * (b * f - e * c)) / det;
  return Number.isFinite(x + y + z) ? [x, y, z] : null;
}

interface Cand { cost: number; a: number; b: number; va: number; vb: number; p: [number, number, number] }

class Heap {
  private h: Cand[] = [];
  get size() { return this.h.length; }
  push(c: Cand) { const h = this.h; h.push(c); let i = h.length - 1; while (i > 0) { const p = (i - 1) >> 1; if (h[p].cost <= h[i].cost) break; [h[p], h[i]] = [h[i], h[p]]; i = p; } }
  pop(): Cand | undefined {
    const h = this.h; if (!h.length) return undefined;
    const top = h[0]; const last = h.pop()!;
    if (h.length) { h[0] = last; let i = 0; for (;;) { const l = 2 * i + 1, r = l + 1; let m = i; if (l < h.length && h[l].cost < h[m].cost) m = l; if (r < h.length && h[r].cost < h[m].cost) m = r; if (m === i) break; [h[m], h[i]] = [h[i], h[m]]; i = m; } }
    return top;
  }
}

/** Reduces `mesh` to about `targetTriangles` triangles (never below 4). Returns the original if already small enough. */
export function simplifyMesh(mesh: Mesh, targetTriangles: number): Mesh {
  const nT0 = mesh.triangles.length;
  const target = Math.max(4, Math.floor(targetTriangles));
  if (nT0 <= target) return mesh;
  const nV = mesh.vertices.length / 3;
  const pos = Float64Array.from(mesh.vertices);
  const tri = new Int32Array(nT0 * 3);
  mesh.triangles.forEach((t, i) => { tri[i * 3] = t[0]; tri[i * 3 + 1] = t[1]; tri[i * 3 + 2] = t[2]; });
  const alive = new Uint8Array(nT0).fill(1);
  const vFaces: Set<number>[] = Array.from({ length: nV }, () => new Set<number>());
  for (let f = 0; f < nT0; f++) for (let k = 0; k < 3; k++) vFaces[tri[f * 3 + k]].add(f);

  const quad: Q[] = Array.from({ length: nV }, () => new Float64Array(10));
  const edgeCount = new Map<number, number>();
  const ekey = (a: number, b: number) => (a < b ? a * nV + b : b * nV + a);
  const normalOf = (f: number, p: Float64Array = pos): [number, number, number, number] => {
    const a = tri[f * 3] * 3, b = tri[f * 3 + 1] * 3, c = tri[f * 3 + 2] * 3;
    const ux = p[b] - p[a], uy = p[b + 1] - p[a + 1], uz = p[b + 2] - p[a + 2], vx = p[c] - p[a], vy = p[c + 1] - p[a + 1], vz = p[c + 2] - p[a + 2];
    const nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx;
    return [nx, ny, nz, Math.hypot(nx, ny, nz)];
  };
  for (let f = 0; f < nT0; f++) {
    const [nx, ny, nz, len] = normalOf(f);
    if (len > 0) {
      const a = tri[f * 3] * 3, d = -(nx / len * pos[a] + ny / len * pos[a + 1] + nz / len * pos[a + 2]);
      const q = planeQ(nx / len, ny / len, nz / len, d, len / 2);
      for (let k = 0; k < 3; k++) quad[tri[f * 3 + k]] = addQ(quad[tri[f * 3 + k]], q);
    }
    for (let k = 0; k < 3; k++) { const key = ekey(tri[f * 3 + k], tri[f * 3 + (k + 1) % 3]); edgeCount.set(key, (edgeCount.get(key) ?? 0) + 1); }
  }
  // Open borders: add a plane through the border edge, perpendicular to its face, so holes don't creep inward.
  for (let f = 0; f < nT0; f++) {
    const [nx, ny, nz, len] = normalOf(f);
    if (!len) continue;
    for (let k = 0; k < 3; k++) {
      const a = tri[f * 3 + k], b = tri[f * 3 + (k + 1) % 3];
      if (edgeCount.get(ekey(a, b)) !== 1) continue;
      const ex = pos[b * 3] - pos[a * 3], ey = pos[b * 3 + 1] - pos[a * 3 + 1], ez = pos[b * 3 + 2] - pos[a * 3 + 2];
      let px = ey * nz - ez * ny, py = ez * nx - ex * nz, pz = ex * ny - ey * nx;
      const pl = Math.hypot(px, py, pz); if (!pl) continue;
      px /= pl; py /= pl; pz /= pl;
      const q = planeQ(px, py, pz, -(px * pos[a * 3] + py * pos[a * 3 + 1] + pz * pos[a * 3 + 2]), 100 * Math.hypot(ex, ey, ez) ** 2 / 2 / 1);
      quad[a] = addQ(quad[a], q); quad[b] = addQ(quad[b], q);
    }
  }

  const version = new Int32Array(nV);
  const removed = new Uint8Array(nV);
  const neighbours = (v: number) => { const s = new Set<number>(); for (const f of Array.from(vFaces[v])) for (let k = 0; k < 3; k++) { const w = tri[f * 3 + k]; if (w !== v) s.add(w); } return s; };

  const candidate = (a: number, b: number): Cand => {
    const q = addQ(quad[a], quad[b]);
    const opt = solve(q);
    const pts: [number, number, number][] = [[pos[a * 3], pos[a * 3 + 1], pos[a * 3 + 2]], [pos[b * 3], pos[b * 3 + 1], pos[b * 3 + 2]]];
    pts.push([(pts[0][0] + pts[1][0]) / 2, (pts[0][1] + pts[1][1]) / 2, (pts[0][2] + pts[1][2]) / 2]);
    if (opt) pts.push(opt);
    let best = pts[0], bc = Infinity;
    for (const p of pts) { const c = evalQ(q, p[0], p[1], p[2]); if (c < bc) { bc = c; best = p; } }
    return { cost: Math.max(0, bc), a, b, va: version[a], vb: version[b], p: best };
  };

  const heap = new Heap();
  const seen = new Set<number>();
  for (let f = 0; f < nT0; f++) for (let k = 0; k < 3; k++) {
    const a = tri[f * 3 + k], b = tri[f * 3 + (k + 1) % 3], key = ekey(a, b);
    if (!seen.has(key)) { seen.add(key); heap.push(candidate(a, b)); }
  }

  let faces = nT0;
  const tmp = new Float64Array(9);
  while (faces > target && heap.size) {
    const c = heap.pop()!;
    const { a, b } = c;
    if (removed[a] || removed[b] || version[a] !== c.va || version[b] !== c.vb) continue;
    // Link condition: the two ends may share only the (one or two) vertices that face the edge, or the surface pinches.
    const na = neighbours(a), nb = neighbours(b);
    let shared = 0; na.forEach((w) => { if (nb.has(w)) shared++; });
    const sharedFaces = Array.from(vFaces[a]).filter((f) => vFaces[b].has(f)).length;
    if (shared !== sharedFaces) continue;
    // No face may flip or collapse to a sliver.
    let ok = true;
    for (const [v, other] of [[a, b], [b, a]] as const) {
      for (const f of Array.from(vFaces[v])) {
        if (vFaces[other].has(f)) continue;
        const [n0x, n0y, n0z, l0] = normalOf(f);
        for (let k = 0; k < 9; k++) tmp[k] = pos[tri[f * 3 + (k / 3 | 0)] * 3 + (k % 3)];
        for (let k = 0; k < 3; k++) if (tri[f * 3 + k] === v) { tmp[k * 3] = c.p[0]; tmp[k * 3 + 1] = c.p[1]; tmp[k * 3 + 2] = c.p[2]; }
        const ux = tmp[3] - tmp[0], uy = tmp[4] - tmp[1], uz = tmp[5] - tmp[2], vx = tmp[6] - tmp[0], vy = tmp[7] - tmp[1], vz = tmp[8] - tmp[2];
        const n1x = uy * vz - uz * vy, n1y = uz * vx - ux * vz, n1z = ux * vy - uy * vx, l1 = Math.hypot(n1x, n1y, n1z);
        if (l0 > 0 && (l1 === 0 || (n0x * n1x + n0y * n1y + n0z * n1z) / (l0 * l1) < 0.2)) { ok = false; break; }
      }
      if (!ok) break;
    }
    if (!ok) continue;

    pos[a * 3] = c.p[0]; pos[a * 3 + 1] = c.p[1]; pos[a * 3 + 2] = c.p[2];
    quad[a] = addQ(quad[a], quad[b]);
    for (const f of Array.from(vFaces[b])) {
      if (vFaces[a].has(f)) { alive[f] = 0; faces--; for (let k = 0; k < 3; k++) vFaces[tri[f * 3 + k]].delete(f); continue; }
      for (let k = 0; k < 3; k++) if (tri[f * 3 + k] === b) tri[f * 3 + k] = a;
      vFaces[b].delete(f); vFaces[a].add(f);
    }
    removed[b] = 1; version[a]++;
    neighbours(a).forEach((w) => heap.push(candidate(a, w)));
  }

  const remap = new Int32Array(nV).fill(-1);
  const vertices: number[] = [];
  const triangles: number[][] = [];
  for (let f = 0; f < nT0; f++) {
    if (!alive[f]) continue;
    const t = [tri[f * 3], tri[f * 3 + 1], tri[f * 3 + 2]];
    if (t[0] === t[1] || t[1] === t[2] || t[0] === t[2]) continue;
    for (let k = 0; k < 3; k++) {
      if (remap[t[k]] < 0) { remap[t[k]] = vertices.length / 3; vertices.push(pos[t[k] * 3], pos[t[k] * 3 + 1], pos[t[k] * 3 + 2]); }
      t[k] = remap[t[k]];
    }
    triangles.push(t);
  }
  return { vertices, triangles };
}
